import { PATHWAY_MENU_TYPES, SECTION_STATUS } from '@routes/sidebar/___shared___/components/pathway-menu/constants.ts';
import type {
    LearningElement,
    LearningElementStatus,
    LearningSection,
} from '@routes/sidebar/learning/___pages___/learning-overview/api/learningOverview.api.ts';
import type {
    PathwaySectionGroup,
    SectionGroup,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/pathway-menu/types.ts';
import { secondsToWholeMinutes } from '@utils/date.ts';

export const isDisabledPathway = (status: LearningElementStatus): boolean => status === SECTION_STATUS.DISABLED;
export const isNewPathway = (status: LearningElementStatus): boolean => status === SECTION_STATUS.NEW;
export const isCompletedOrRefresherPathway = (status: LearningElementStatus): boolean =>
    [SECTION_STATUS.COMPLETED, SECTION_STATUS.REFRESHER].includes(status);

export const isFinalQuizReady = (learningElements: LearningElement[] | null, element: LearningElement): boolean => {
    const list = learningElements ?? [];

    // We only care about readiness for final quizzes.
    // For all other types, there's no blocking condition — they're always considered "ready".
    if (element.learningElementType !== PATHWAY_MENU_TYPES.FINAL_QUIZ) {
        return true;
    }

    const idx = list.findIndex((el) => el.id === element.id);
    const prevSlice = idx >= 0 ? list.slice(0, idx) : list.slice(0, -1);

    return prevSlice.every((el) => isCompletedOrRefresherPathway(el.learningElementState));
};

export const isFinalQuizAccessBlockedAfterNoAttempts = (
    learningElements: LearningElement[],
    element: LearningElement,
    isBlockedAfterNoAttempts: boolean,
): boolean => {
    if (!isBlockedAfterNoAttempts || element.learningElementType !== PATHWAY_MENU_TYPES.FINAL_QUIZ) {
        return false;
    }

    return !isFinalQuizReady(learningElements, element);
};

export const getSectionStatus = (items: LearningElement[]): LearningElementStatus => {
    const states = items.map((i) => i.learningElementState);

    if (states.includes(SECTION_STATUS.REFRESHER)) return SECTION_STATUS.REFRESHER;

    const allCompleted = states.every((s) => s === SECTION_STATUS.COMPLETED);
    if (allCompleted) return SECTION_STATUS.COMPLETED;

    const allNew = states.every((s) => s === SECTION_STATUS.NEW);
    if (allNew) return SECTION_STATUS.NEW;

    const allDisabled = states.every((s) => s === SECTION_STATUS.DISABLED);
    if (allDisabled) return SECTION_STATUS.DISABLED;

    return SECTION_STATUS.IN_PROGRESS;
};

export const isSectionFullyComplete = (items: LearningElement[]): boolean =>
    items.every((el) => isCompletedOrRefresherPathway(el.learningElementState));

const getSectionItems = (section: LearningSection): LearningElement[] => [
    ...(section.elements ?? []),
    ...(section.subSections ?? []).flatMap(getSectionItems),
];

const getSectionGroups = (section: LearningSection): SectionGroup[] => {
    const groups: SectionGroup[] = [];

    if (section.elements.length > 0) {
        groups.push({
            name: null,
            items: section.elements,
        });
    }

    section.subSections.forEach((subSection) => {
        groups.push({
            name: subSection.name,
            items: getSectionItems(subSection),
        });
    });

    return groups;
};

export const buildSectionGroups = (sections: LearningSection[]): PathwaySectionGroup[] => {
    return sections.map((section) => {
        const name = section.name ?? '';
        const items = getSectionItems(section);
        const status = getSectionStatus(items);
        const seconds = items.reduce((acc, i) => acc + (i.durationInSeconds ?? 0), 0);
        const minutes = secondsToWholeMinutes(seconds);
        const sectionGroups = getSectionGroups(section);

        return { name, items, status, minutes, sections: sectionGroups };
    });
};

/** Returns the Collapse key (1-based string) for the section that contains lastVisitedElementId. */
export const getDefaultOpenSectionKey = (
    lastVisitedElementId: number | null | undefined,
    sections: PathwaySectionGroup[],
): string => {
    if (!lastVisitedElementId) return '1';
    const idx = sections.findIndex((section) => section.items.some((i) => i.id === lastVisitedElementId));
    return idx >= 0 ? String(idx + 1) : '1';
};

export const excludeAdaptiveQuiz = <T extends { learningElementType?: string }>(le: T): boolean =>
    le.learningElementType !== PATHWAY_MENU_TYPES.ADAPTIVE_QUIZ;

export const getNonDisabledFinalQuizElement = (learningElements: LearningElement[]) =>
    learningElements.find(
        (el) => el.learningElementType === PATHWAY_MENU_TYPES.FINAL_QUIZ && !isDisabledPathway(el.learningElementState),
    );

const INCOMPLETE_PATHWAY_ITEM_STATUSES: LearningElementStatus[] = [
    SECTION_STATUS.NEW,
    SECTION_STATUS.REFRESHER,
    SECTION_STATUS.IN_PROGRESS,
    SECTION_STATUS.RESTARTED,
];

export const getFirstNonDisabledNonFinalQuizElement = (learningElements: LearningElement[]) => {
    return learningElements
        .filter(excludeAdaptiveQuiz)
        .find(
            (item) =>
                item.learningElementType !== PATHWAY_MENU_TYPES.FINAL_QUIZ &&
                !isDisabledPathway(item.learningElementState) &&
                INCOMPLETE_PATHWAY_ITEM_STATUSES.includes(item.learningElementState),
        );
};
